import React from 'react';
import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { buttonFocusStyle } from '../buttons/common.styles';

type PositionProps = {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
};

type ActionButton = {
  floating: boolean;
  size: 'medium' | 'large';
};

export const getPositionStyle = (coordinates: PositionProps): React.CSSProperties =>
  Object.fromEntries(
    Object.entries(coordinates)
      .filter(([, v]) => v != null)
      .map(([k, v]) => [k, `${v}px`])
  );

const actionButtonMediumStyle = css`
  min-width: 40px;
  width: 40px;
  height: 40px;
`;

const actionButtonLargeStyle = css`
  min-width: 56px;
  width: 56px;
  height: 56px;
`;

const actionButtonBaseStyle = css`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.neutral20};
  border-radius: 50%;
  color: ${theme.colors.black};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 250ms ease-out;

  &:focus,
  &:hover {
    color: ${theme.colors.black};
  }

  &:focus {
    background: ${theme.colors.neutral20};
  }

  &:hover:not([disabled]) {
    background: ${theme.colors.neutral20};
  }

  &:active {
    color: white;
    background: ${theme.colors.primary50};
    box-shadow: none;
  }

  &:disabled {
    color: ${theme.colors.neutral20};
    background: ${theme.colors.neutral5};
    cursor: default;
  }
`;

const actionButtonFloatingStyle = css`
  box-shadow: ${theme.shadows.heavy};
  position: absolute;
  background: ${theme.colors.primary50};
  color: white;

  &:focus,
  &:hover {
    color: ${theme.colors.white};
  }

  &:focus {
    background: ${theme.colors.primary50};
  }

  &:hover {
    background: ${theme.colors.primary70};
  }

  &:active {
    background: ${theme.colors.primary80};
  }

  &:disabled {
    color: ${theme.colors.neutral20};
    background: ${theme.colors.neutral5};
    cursor: default;
  }
`;

export const getActionButtonClassName = ({ floating, size }: ActionButton): string =>
  cx(
    actionButtonBaseStyle,
    buttonFocusStyle,
    size === 'large' ? actionButtonLargeStyle : actionButtonMediumStyle,
    floating && actionButtonFloatingStyle
  );
